import React, { useState, type JSX } from 'react';

import DeviceConfigureForm from '../DeviceConfigureForm/DeviceConfigureForm';
import { DeviceRosterOverlay } from './DeviceRosterOverlay';

import {
  TABLE_BODY_CLASS,
  TABLE_HEAD_CELL_CLASS,
  TABLE_HEAD_STICKY_CLASS,
  VALIDATION_REPORT_FIRST_CELL_FAIL_CLASS,
  VALIDATION_REPORT_FIRST_CELL_OK_CLASS,
  VALIDATION_REPORT_ROW_FAIL_CLASS,
  VALIDATION_REPORT_ROW_OK_CLASS,
  VALIDATION_REPORT_TABLE_CLASS,
} from './tableDesign';
import type { ValidationFieldCell } from '../../helpers/deviceValidation';
import type { RaceValidationReportRow } from './types/ValidationReportTypes';

function toCsvCell(value: string): string {
  const safe = String(value ?? '').replace(/"/g, '""');
  return `"${safe}"`;
}

function csvResult(cell: ValidationFieldCell): string {
  if (cell.result === 'pass') return 'pass';
  if (cell.result === 'fail') return 'fail';
  return 'n/a';
}

/** CSV matching the validation report table columns (same as Events → validation report). */
export function buildRaceValidationReportCsv(params: {
  eventId: string;
  raceId: string;
  rows: RaceValidationReportRow[];
}): string {
  const { eventId, raceId, rows } = params;
  const header = [
    'event_id',
    'race_id',
    'device_id',
    'front_device_id',
    'data_freshness',
    'firmware',
    'front_firmware',
    'battery',
    'debug_log',
    'race_log',
    'lifecycle',
    'verification_detail',
  ].join(',');
  const lines = rows.map(({ deviceId, frontDeviceId, breakdown: b }) =>
    [
      toCsvCell(eventId),
      toCsvCell(raceId),
      toCsvCell(deviceId),
      toCsvCell(frontDeviceId ?? ''),
      toCsvCell(csvResult(b.freshness)),
      toCsvCell(csvResult(b.firmware)),
      toCsvCell(csvResult(b.firmwareFront)),
      toCsvCell(csvResult(b.battery)),
      toCsvCell(csvResult(b.debugLog)),
      toCsvCell(csvResult(b.raceLog)),
      toCsvCell(csvResult(b.lifecycle)),
      toCsvCell(b.notes),
    ].join(','),
  );
  return [header, ...lines].join('\n');
}

export function ValidationFieldCellMark({ cell }: { cell: ValidationFieldCell }): JSX.Element {
  if (cell.result === 'na') {
    return (
      <span className="text-gray-400 dark:text-slate-400" title={cell.detail}>
        —
      </span>
    );
  }
  if (cell.result === 'pass') {
    return (
      <span className="font-bold text-emerald-700" title="Pass">
        ✓
      </span>
    );
  }
  return (
    <span className="font-bold text-red-700" title={cell.detail}>
      ✗
    </span>
  );
}

type TableProps = {
  rows: RaceValidationReportRow[];
  /** Event context for append-only device notes (required to save). */
  eventId?: string;
  notesDisabled?: boolean;
  /** Show per-device Add note column (default true). */
  showDeviceNotes?: boolean;
};

const NOTE_BTN =
  'rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

/** Shared validation breakdown table (Events dialog + Devices validation report). */
export function RaceValidationReportTable(props: TableProps): JSX.Element {
  const { rows, eventId, notesDisabled = false, showDeviceNotes = true } = props;
  const [noteDialogDeviceId, setNoteDialogDeviceId] = useState<string | null>(null);
  const [rosterDeviceId, setRosterDeviceId] = useState<string | null>(null);
  const eventIdTrimmed = String(eventId ?? '').trim();
  const showNotesColumn = showDeviceNotes;
  const canSaveNotes = !!eventIdTrimmed;

  return (
    <>
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
      <table className={VALIDATION_REPORT_TABLE_CLASS}>
        <thead className={TABLE_HEAD_STICKY_CLASS}>
          <tr>
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[7rem] whitespace-nowrap pl-7`}>Device ID</th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Device status heard within the last 5 minutes">
              Fresh
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Firmware matches race configuration">
              FW
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Paired front device's firmware matches race configuration">
              Front FW
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Battery meets minimum (mV)">
              Battery
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Debug log size within limit (bytes)">
              Debug log
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Race log size within limit (bytes)">
              Race log
            </th>
            <th className={`${TABLE_HEAD_CELL_CLASS} text-center`} title="Device lifecycle in service">
              Lifecycle
            </th>
            <th className={TABLE_HEAD_CELL_CLASS}>Verification detail</th>
            {showNotesColumn ? <th className={`${TABLE_HEAD_CELL_CLASS} text-center`}>Notes</th> : null}
          </tr>
        </thead>
        <tbody className={TABLE_BODY_CLASS}>
          {rows.map(({ deviceId, frontDeviceId, breakdown: b }) => {
            const rowWarn = !!b.notes;
            const frontId = String(frontDeviceId ?? '').trim();
            return (
              <tr
                key={frontId ? `${deviceId}#${frontId}` : deviceId}
                className={rowWarn ? VALIDATION_REPORT_ROW_FAIL_CLASS : VALIDATION_REPORT_ROW_OK_CLASS}
              >
                <td
                  className={[
                    'min-w-[11rem] max-w-[13rem] py-2 pl-3 pr-4 align-top',
                    rowWarn ? VALIDATION_REPORT_FIRST_CELL_FAIL_CLASS : VALIDATION_REPORT_FIRST_CELL_OK_CLASS,
                  ].join(' ')}
                >
                  <button
                    type="button"
                    className="overflow-x-auto font-mono text-xs text-gray-900 dark:text-slate-100 whitespace-nowrap hover:underline cursor-pointer text-left"
                    title={deviceId}
                    onClick={() => setRosterDeviceId(deviceId)}
                  >
                    {deviceId}
                  </button>
                  {frontId ? (
                    <div
                      className="mt-1 overflow-x-auto whitespace-nowrap font-mono text-[10px] text-blue-700 dark:text-blue-300"
                      title={`Paired front device ${frontId}`}
                    >
                      paired front: {frontId}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.freshness} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.firmware} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.firmwareFront} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.battery} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.debugLog} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.raceLog} />
                </td>
                <td className="px-4 py-2 text-center align-middle">
                  <ValidationFieldCellMark cell={b.lifecycle} />
                </td>
                <td className="max-w-md px-4 py-2 text-xs text-gray-800 dark:text-slate-200">
                  {b.notes ? (
                    <span className="text-red-900 dark:text-red-300">{b.notes}</span>
                  ) : (
                    <span className="text-emerald-800 dark:text-emerald-300">All checks passed</span>
                  )}
                </td>
                {showNotesColumn ? (
                  <td className="px-4 py-2 text-center align-middle whitespace-nowrap">
                    <button
                      type="button"
                      className={NOTE_BTN}
                      disabled={notesDisabled}
                      onClick={() => setNoteDialogDeviceId(deviceId)}
                    >
                      Add note
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {showNotesColumn && canSaveNotes && noteDialogDeviceId ? (
      <DeviceConfigureForm
        selectedIds={[noteDialogDeviceId]}
        initialOperation="LEAVE_DEVICE_NOTE"
        initialNoteEventId={eventIdTrimmed}
        overlayZIndexClass="z-[5400]"
        onClose={() => setNoteDialogDeviceId(null)}
      />
    ) : null}
    <DeviceRosterOverlay
      deviceId={rosterDeviceId}
      operatorScopedEventId={eventIdTrimmed || null}
      onClose={() => setRosterDeviceId(null)}
    />
    </>
  );
}
